import { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { 
  updatePassword, 
  reauthenticateWithCredential,
  EmailAuthProvider,
  sendEmailVerification,
  verifyBeforeUpdateEmail,
  reload
} from 'firebase/auth';

import { auth } from '../utils/firebase/firebase.utils';
import { logUserAction, storePendingEmailChange } from '../utils/firebase/firebase.utils';

export const useUpdateAccount = () => {
  const [error, setError] = useState(null);
  const [isPending, setIsPending] = useState(false);


  const updateUserEmail = async (newEmail, oldEmail, password) => {
    if (!auth.currentUser) {
      setError('No user is signed in');
      return;
    }

    if (newEmail === oldEmail) {
      return; // No change needed
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setError('Please enter a valid email address');
      return { success: false, error: 'Please enter a valid email address' };
    }

    setIsPending(true);
    setError(null);

    try {
      // Check if email is already in use by another account
      const functions = getFunctions();
      const checkEmailAvailability = httpsCallable(functions, 'checkEmailAvailability');
      const availabilityResult = await checkEmailAvailability({ email: newEmail });
      if (!availabilityResult.data.isAvailable) {
        setError(availabilityResult.data.message || 'An account already exists with that email.');
        setIsPending(false);
        return { success: false, error: availabilityResult.data.message || 'An account already exists with that email.' };
      }

      // Re-authenticate before email update (required by Firebase)
      if (!password) {
        setError('Password is required for email update. Please enter your current password.');
        setIsPending(false);
        return { success: false, error: 'Password is required for email update. Please enter your current password.' };
      }
      
      const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
      await reauthenticateWithCredential(auth.currentUser, credential);
      
      await verifyBeforeUpdateEmail(auth.currentUser, newEmail);

      // Store pending change - activity will be logged only when user confirms (clicks link)
      await storePendingEmailChange(auth.currentUser.uid, oldEmail, newEmail);

      await reload(auth.currentUser);

      setIsPending(false);
      return { success: true, message: `Verification email sent to ${newEmail}. Please check your new email and click the verification link to complete the email change.` };
    } catch (err) {
      if (err.code === "auth/operation-not-allowed") {
        // Handle email verification requirement for NEW email
        setError(`Email update failed: Email/Password authentication may be disabled in Firebase Console. Please check Authentication > Sign-in method and ensure Email/Password is enabled.`);
        setIsPending(false);
        return { success: false, error: `Email update failed: Email/Password authentication may be disabled in Firebase Console. Please check Authentication > Sign-in method and ensure Email/Password is enabled.` };
      } else {
        setError(err.message);
        setIsPending(false);
        return { success: false, error: err.message };
      }
    }
  };

  const updateUserPassword = async (newPassword) => {
    if (!auth.currentUser) {
      setError('No user is signed in');
      return;
    }

    setIsPending(true);
    setError(null);

    try {
      await updatePassword(auth.currentUser, newPassword);

      // Log the action
      await logUserAction(auth.currentUser.uid, "updatedAccount", {
        accountActionType: "password"
      });

      setIsPending(false);
      return { success: true };
    } catch (err) {
      if (err.code === "auth/requires-recent-login") {
        // Handle re-authentication
        setError('Re-authentication required for password update. Please sign out and sign back in.');
        setIsPending(false);
        return { success: false, error: 'Re-authentication required for password update. Please sign out and sign back in.' };
      } else {
        setError(err.message);
        setIsPending(false);
        return { success: false, error: err.message };
      }
    }
  };


  const sendVerificationEmail = async () => {
    if (!auth.currentUser) {
      setError('No user is signed in');
      return { success: false, error: 'No user is signed in' };
    }

    try {
      await sendEmailVerification(auth.currentUser);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const refreshUserEmailVerification = async () => {
    if (!auth.currentUser) {
      return { success: false, error: 'No user is signed in' };
    }

    try {
      await reload(auth.currentUser);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const clearError = () => {
    setError(null);
  };

  return { 
    updateUserEmail, 
    updateUserPassword, 
    sendVerificationEmail,
    refreshUserEmailVerification,
    error, 
    isPending, 
    clearError 
  };
}; 